import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { HiOutlineSearch } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../../contexts/theme-context';
import { contactsData } from '../../../data/contactsData';
import { certificationsData, educationData } from '../../../data/educationData';
import { experienceData } from '../../../data/experienceData';
import { headerData } from '../../../data/headerData';
import { projectsData } from '../../../data/projectsData';
import { skillsData } from '../../../data/skillsData';
import { canNativeShare, saveContact, shareSite } from '../../../utils/contactCard';
import { accents } from '../../../data/accents';
import { currentAccent, setAccent } from '../../../utils/accent';
import { downloadResumeJson } from '../../../utils/jsonResume';
import { canInstall, onInstallChange, promptInstall } from '../../../utils/installPrompt';
import { buildIndex, search } from '../../../utils/siteSearch';
import { withViewTransition } from '../../../utils/viewTransition';
import { OPEN_TERMINAL_EVENT } from '../terminal/terminal-events';
import './command-palette.css';

export const OPEN_PALETTE_EVENT = 'open-command-palette';

export const shortcutLabel = () => {
    const platform = typeof navigator !== 'undefined' ? navigator.platform || '' : '';
    return /mac|iphone|ipad/i.test(platform) ? '⌘K' : 'Ctrl K';
};

const sections = [
    ['about', 'About', 'who I am and what I focus on'],
    ['experience', 'Experience', 'roles and work history'],
    ['skills', 'Skills', 'technical skills toolkit stack'],
    ['match', 'Role match', 'job description fit compare hiring recruiter'],
    ['architecture', 'How I build', 'architecture diagram microservices redis'],
    ['security', 'Security architecture', 'jwt rs256 tokens authentication authorization rbac zero trust attack demo'],
    ['vision', 'Computer vision lab', 'filters sobel edges image processing demo'],
    ['projects', 'Projects', 'featured selected work'],
    ['education', 'Education', 'degree certifications'],
    ['contacts', 'Contact', 'email reach hire'],
];

const matches = (command, query) => {
    const haystack = `${command.label} ${command.group} ${command.keywords || ''}`.toLowerCase();
    return query.toLowerCase().split(/\s+/).filter(Boolean).every((word) => haystack.includes(word));
};

function CommandPalette() {
    const navigate = useNavigate();
    const { changeTheme, isDark } = useContext(ThemeContext);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [toast, setToast] = useState('');
    const [installable, setInstallable] = useState(canInstall);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const returnFocusRef = useRef(null);

    useEffect(() => onInstallChange(setInstallable), []);

    const showToast = useCallback((message) => {
        setToast(message);
        setTimeout(() => setToast(''), 2200);
    }, []);

    const goToSection = useCallback((id) => {
        const scroll = () => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView();
        };
        if (window.location.pathname === '/') {
            scroll();
        } else {
            withViewTransition(() => navigate('/'), 'route');
            setTimeout(scroll, 450);
        }
    }, [navigate]);

    const commands = useMemo(() => {
        const list = [
            ...sections.map(([id, label, keywords]) => ({
                id: `go-${id}`, group: 'Go to', label, keywords, hint: 'Section', run: () => goToSection(id),
            })),
            { id: 'go-projects-page', group: 'Go to', label: 'All projects', keywords: 'projects search page', hint: 'Page', run: () => withViewTransition(() => navigate('/projects'), 'route') },
            { id: 'go-resume', group: 'Go to', label: 'Résumé page', keywords: 'resume cv curriculum vitae printable pdf ats', hint: 'Page', run: () => withViewTransition(() => navigate('/resume'), 'route') },
            {
                id: 'print', group: 'Actions', label: 'Print or save as PDF',
                keywords: 'print pdf cv resume download save', hint: 'Opens print dialog', run: () => window.print(),
            },
            {
                id: 'theme', group: 'Actions', label: isDark ? 'Switch to light theme' : 'Switch to dark theme',
                keywords: 'theme dark light mode appearance', hint: 'Theme', run: changeTheme,
            },
            {
                id: 'save-contact', group: 'Actions', label: 'Save contact card (vCard)',
                keywords: 'vcard vcf contact address book phone download', hint: 'Adds to your contacts',
                run: () => { saveContact(); showToast('Contact card downloaded'); },
            },
            ...accents.map((accent) => ({
                id: `accent-${accent.id}`, group: 'Actions',
                label: `Accent colour: ${accent.label}${currentAccent() === accent.id ? ' (current)' : ''}`,
                keywords: 'accent colour color theme personalize appearance', hint: 'Remembered on this device',
                run: () => { setAccent(accent.id); showToast(`${accent.label} accent`); },
            })),
            {
                id: 'terminal', group: 'Actions', label: 'Open terminal',
                keywords: 'terminal shell console commands whoami cli type ask', hint: 'Ask me anything, shell style',
                run: () => window.dispatchEvent(new Event(OPEN_TERMINAL_EVENT)),
            },
            {
                id: 'resume-json', group: 'Actions', label: 'Download résumé as JSON',
                keywords: 'json resume cv ats recruiter machine readable import download', hint: 'JSON Resume format',
                run: () => { downloadResumeJson(); showToast('Résumé JSON downloaded'); },
            },
            {
                id: 'share', group: 'Actions', label: canNativeShare() ? 'Share this site' : 'Copy link to this site',
                keywords: 'share link url send copy', hint: canNativeShare() ? 'Opens share sheet' : 'Clipboard',
                run: async () => {
                    const outcome = await shareSite();
                    if (outcome === 'copied') showToast('Link copied');
                    if (outcome === 'failed') showToast('Could not share. Copy the address bar instead.');
                },
            },
        ];
        if (installable) {
            list.push({
                id: 'install', group: 'Actions', label: 'Install as an app',
                keywords: 'install pwa app offline home screen desktop', hint: 'Works offline',
                run: async () => {
                    const outcome = await promptInstall();
                    if (outcome === 'accepted') showToast('Installed. Open it from your apps.');
                },
            });
        }
        if (contactsData.email) {
            list.push(
                {
                    id: 'copy-email', group: 'Actions', label: 'Copy email address', keywords: contactsData.email, hint: 'Clipboard',
                    run: async () => {
                        try {
                            await navigator.clipboard.writeText(contactsData.email);
                            showToast('Email address copied');
                        } catch (e) {
                            showToast('Could not copy. Use the Contact section.');
                        }
                    },
                },
                { id: 'send-email', group: 'Actions', label: 'Send an email', keywords: 'mail contact hire', hint: 'Opens mail app', run: () => { window.location.href = `mailto:${contactsData.email}`; } },
            );
        }
        const external = (id, label, href, keywords) => href && list.push({
            id, group: 'Links', label, keywords, hint: 'Opens in a new tab', run: () => window.open(href, '_blank', 'noopener,noreferrer'),
        });
        external('github', 'GitHub profile', contactsData.github, 'code repositories');
        external('linkedin', 'LinkedIn profile', contactsData.linkedIn, 'network');
        external('resume', 'Resume (PDF)', headerData.resumePdf, 'cv download');
        external('stackoverflow', 'Stack Overflow', contactsData.stackOverflow, 'questions answers');
        external('medium', 'Medium articles', contactsData.medium, 'blog writing');
        return list;
    }, [changeTheme, goToSection, installable, isDark, navigate, showToast]);

    // On-device search across roles, projects, skills and training (utils/siteSearch.js)
    const index = useMemo(() => buildIndex({
        experience: experienceData,
        projects: projectsData,
        skills: skillsData,
        education: educationData,
        certifications: certificationsData,
    }), []);

    const results = useMemo(() => {
        const trimmed = query.trim();
        if (!trimmed) return commands;
        const matchingCommands = commands.filter((command) => matches(command, trimmed));
        const content = trimmed.length < 2 ? [] : search(trimmed, index, 8).map((hit) => ({
            id: `res-${hit.id}`,
            group: 'Search results',
            label: hit.title,
            detail: hit.detail,
            hint: hit.type,
            run: () => goToSection(hit.section),
        }));
        return [...matchingCommands, ...content];
    }, [commands, goToSection, index, query]);

    const openPalette = useCallback(() => {
        returnFocusRef.current = document.activeElement;
        setQuery('');
        setActiveIndex(0);
        setOpen(true);
    }, []);

    const closePalette = useCallback(() => {
        setOpen(false);
        const target = returnFocusRef.current;
        if (target && typeof target.focus === 'function') target.focus();
    }, []);

    const run = (command) => {
        closePalette();
        // Let the dialog unmount (and restore body scroll) before acting.
        setTimeout(() => command.run(), 0);
    };

    // Global shortcut + open event from the navbar button.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                if (open) closePalette(); else openPalette();
            }
        };
        document.addEventListener('keydown', onKey);
        window.addEventListener(OPEN_PALETTE_EVENT, openPalette);
        return () => {
            document.removeEventListener('keydown', onKey);
            window.removeEventListener(OPEN_PALETTE_EVENT, openPalette);
        };
    }, [open, openPalette, closePalette]);

    // Focus the field and lock page scroll while open.
    useEffect(() => {
        if (!open) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        if (inputRef.current) inputRef.current.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    // Keep the highlighted row in view.
    useEffect(() => {
        if (!open || !listRef.current) return;
        const el = listRef.current.querySelector('[aria-selected="true"]');
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, open, results]);

    const onInputKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (results.length ? (i + 1) % results.length : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (results[activeIndex]) run(results[activeIndex]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            closePalette();
        } else if (e.key === 'Tab') {
            e.preventDefault(); // single focus target: keep focus inside the dialog
        }
    };

    const activeCommand = results[activeIndex];

    return (
        <>
            {open && (
                <div className='cmdk' onMouseDown={(e) => e.target === e.currentTarget && closePalette()}>
                    <div className='cmdk__panel' role='dialog' aria-modal='true' aria-label='Command menu'>
                        <div className='cmdk__search'>
                            <HiOutlineSearch aria-hidden='true' />
                            <input
                                ref={inputRef}
                                type='text'
                                role='combobox'
                                aria-expanded='true'
                                aria-controls='cmdk-list'
                                aria-activedescendant={activeCommand ? `cmdk-opt-${activeCommand.id}` : undefined}
                                aria-label='Search commands'
                                placeholder='Search the portfolio…'
                                autoComplete='off'
                                spellCheck='false'
                                value={query}
                                onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
                                onKeyDown={onInputKeyDown}
                            />
                            <kbd>Esc</kbd>
                        </div>

                        <ul id='cmdk-list' ref={listRef} className='cmdk__list' role='listbox' aria-label='Commands'>
                            {results.length === 0 && <li className='cmdk__empty' role='presentation'>No matches for “{query}”.</li>}
                            {results.map((command, index) => (
                                <React.Fragment key={command.id}>
                                    {(index === 0 || results[index - 1].group !== command.group) && (
                                        <li className='cmdk__group' role='presentation'>{command.group}</li>
                                    )}
                                    <li
                                        id={`cmdk-opt-${command.id}`}
                                        role='option'
                                        aria-selected={index === activeIndex}
                                        className={`cmdk__item${index === activeIndex ? ' is-active' : ''}`}
                                        onMouseMove={() => index !== activeIndex && setActiveIndex(index)}
                                        onClick={() => run(command)}
                                    >
                                        <span className='cmdk__main'>
                                            <span>{command.label}</span>
                                            {command.detail && <small className='cmdk__detail'>{command.detail}</small>}
                                        </span>
                                        <span className='cmdk__hint'>{command.hint}</span>
                                    </li>
                                </React.Fragment>
                            ))}
                        </ul>

                        <div className='cmdk__footer' aria-hidden='true'>
                            <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
                            <span><kbd>↵</kbd> select</span>
                            <span><kbd>{shortcutLabel()}</kbd> toggle</span>
                        </div>
                    </div>
                </div>
            )}
            <div className='cmdk__toast' role='status' aria-live='polite'>{toast}</div>
        </>
    );
}

export default CommandPalette;
